import { useSession } from './session';
import { backofficeModules, moduleNav } from './modules/registry';

/**
 * Fachliche Gruppen der Einrichtung (KON-105), in dieser Reihenfolge. Die Startseite der Einrichtung und die Navigation links ordnen die Bereiche danach.
 */
export const SETUP_GROUPS = [
  { key: 'general', label: 'Allgemein' },
  { key: 'inquiries', label: 'Anfragen' },
  { key: 'contacts', label: 'Kontakte' },
  { key: 'products', label: 'Produkte' },
  { key: 'finance', label: 'Finanzen' },
  { key: 'team', label: 'Personalmanagement' },
  { key: 'integrations', label: 'Schnittstellen' },
] as const;
export type SetupGroup = (typeof SETUP_GROUPS)[number]['key'];

/**
 * Bereiche der Einrichtung. Sie liegen nicht in der Seitenleiste, sondern hinter dem Zahnrad in der Kopfleiste.
 * Ein Bereich erscheint nur, wenn sein Modul lizenziert ist und die Rolle das Lesen erlaubt.
 * "built" ist false, solange es für den Bereich nur eine Platzhalterseite gibt.
 */
export interface SetupSection {
  path: string;
  label: string;
  module: string;
  permission: string;
  description: string;
  built: boolean;
  /** Fachliche Gruppe, unter der der Bereich steht */
  group: SetupGroup;
  /** Weitere Suchwörter, die weder in Bezeichnung noch Beschreibung stehen, zum Beispiel „IBAN“ für die Firmendaten */
  keywords?: string[];
  /** Weiteres Modul, das lizenziert sein muss, damit der Bereich Sinn ergibt (die Terminarten setzen den Kalender voraus). Bei einer Liste genügt eines davon. */
  requires?: string | string[];
  /** Nur für Mandanten dieser Branche (zum Beispiel `chauffeur`) */
  industry?: string;
}

const CORE_SECTIONS: readonly SetupSection[] = [
  {
    path: 'anfragetypen',
    label: 'Anfragetypen',
    module: 'setup',
    permission: 'setup.read',
    description: 'Welche Felder eine Anfrage hat, zum Beispiel Auszugsadresse und Wohnfläche beim Umzug. Die Webseite sendet die Felder unter ihrer Kennung.',
    built: true,
    group: 'inquiries',
    keywords: ['Formular', 'Felder', 'Webseite', 'Pflichtfeld'],
  },
  {
    path: 'wirkia-konto',
    label: 'Wirkia Konto',
    module: 'setup',
    permission: 'setup.read',
    description: 'Ihr Konto bei Wirkia: Paket, Auf- und Herabstufen, Zusatzbenutzer buchen und kündigen, Zahlungen und Rechnungen.',
    built: true,
    group: 'general',
    keywords: ['Paket', 'Lizenz', 'Version', 'Abonnement', 'Rechnung', 'Zahlung', 'Kündigen', 'Herunterstufen', 'Zusatzbenutzer', 'Plätze', 'Preis', 'Bestellen', 'Anbieter'],
  },
  {
    path: 'benutzer',
    label: 'Benutzer',
    module: 'users',
    permission: 'users.read',
    description: 'Zugänge zu Wirkia: einladen, Rolle zuweisen und deaktivieren. Zusatzbenutzer lassen sich hier über „Benutzerlizenzen kaufen“ buchen.',
    built: true,
    group: 'general',
    keywords: ['Einladung', 'Passwort', 'Zugang', 'Anmeldung', 'Konto', 'Zusatzbenutzer', 'Plätze', 'Lizenz'],
  },
  {
    path: 'rollen',
    label: 'Rollen & Rechte',
    module: 'users',
    permission: 'users.read',
    description: 'Rollen anlegen, umbenennen und festlegen, was sie in welchem Modul dürfen.',
    built: true,
    group: 'general',
    keywords: ['Rollen', 'Rechte', 'Berechtigungen', 'Zugriff'],
  },
  {
    path: 'speicher',
    label: 'Speicherverwaltung',
    module: 'setup',
    permission: 'setup.storage',
    description: 'Wie viel Speicher belegt ist (Dateien und Datenbank), Zusatzspeicher buchen und alle Dateien ansehen und löschen.',
    built: true,
    group: 'general',
    keywords: ['Speicher', 'Speicherplatz', 'Platz', 'Dateien', 'Löschen', 'Kontingent', 'GB', 'Buchen', 'Aufpreis', 'Datenbank'],
  },
  {
    path: 'nummernkennungen',
    label: 'Nummernkennungen',
    module: 'setup',
    permission: 'setup.read',
    description: 'Womit Ihre Nummern beginnen: eigene Kennungen für Rechnungen, Gutschriften, Angebote, Aufträge, Anfragen, Verträge, Personalnummern, Fahrzeuge und alles andere mit laufender Nummer.',
    built: true,
    group: 'general',
    keywords: ['Präfix', 'Prefix', 'Kennung', 'Nummer', 'Nummernkreis', 'Nummernserie', 'RE', 'Rechnungsnummer', 'Personalnummer', 'Angebotsnummer'],
  },
  {
    path: 'daten-loeschen',
    label: 'Daten löschen',
    module: 'setup',
    permission: 'setup.wipe',
    description: 'Alle Daten Ihres Unternehmens endgültig löschen und das Konto auf den Stand ohne Daten zurücksetzen. Einzelne Einträge blenden Sie über „Archivieren“ aus.',
    built: true,
    group: 'general',
    keywords: ['Löschen', 'Zurücksetzen', 'Reset', 'DSGVO', 'Datenschutz', 'Archivieren', 'Neu beginnen', 'Konto', 'Daten'],
  },
  {
    path: 'fahrtrechnung',
    label: 'Rechnung nach der Fahrt',
    module: 'setup',
    permission: 'setup.read',
    requires: 'invoices_out',
    industry: 'chauffeur',
    description: 'Wenn der Fahrer eine Fahrt abschließt, erstellt Wirkia die Rechnung und schickt sie dem Kunden: Automatik ein oder aus und der Text der Mail.',
    built: true,
    group: 'finance',
    keywords: ['Rechnung', 'Fahrt', 'Mail', 'Text', 'Automatik', 'Gutschein', 'Aktionscode', 'Fahrer', 'Abschluss', 'Versand'],
  },
  {
    path: 'api',
    label: 'APIs und Webhooks',
    module: 'setup',
    permission: 'setup.integrations',
    description: 'Verbindungen zu Diensten außerhalb von Wirkia: Ihr E-Mail-Postfach für den Versand, Stripe und PayPal für Zahlungslinks, Facebook, LinkedIn und TikTok. Dazu die Anbindung Ihrer Webseite: Schlüssel für Anfrageformular, Chat und Newsletter-Anmeldung, soweit Ihr Paket sie enthält.',
    built: true,
    group: 'integrations',
    keywords: ['Schnittstelle', 'Integration', 'E-Mail', 'Postfach', 'SMTP', 'Versand', 'Stripe', 'PayPal', 'Facebook', 'LinkedIn', 'TikTok', 'Zahlung', 'Webhook', 'Schlüssel', 'API-Key', 'Webseite', 'Formular', 'Kontaktformular', 'Chat', 'Webchat', 'Plugin', 'WordPress', 'Newsletter', 'Anfragen senden', 'env', 'Webentwickler'],
  },
];

/** Die festen Bereiche und die, die Module beisteuern (siehe modules/registry.tsx) */
export const SETUP_SECTIONS: readonly SetupSection[] = [...CORE_SECTIONS, ...backofficeModules.flatMap((m) => m.setupSections ?? [])];

/**
 * Module, die nur in der Einrichtung stehen und deshalb nicht in der Seitenleiste erscheinen. Hat ein Modul eigene Einträge in der Seitenleiste (Webchat: „Live Chats“),
 * bleibt es dort, auch wenn es zusätzlich einen Bereich in der Einrichtung hat.
 */
export const SETUP_MODULES: ReadonlySet<string> = new Set(SETUP_SECTIONS.map((s) => s.module).filter((key) => !moduleNav[key]));

/** Bereiche, die der angemeldete Benutzer sehen darf, nach Gruppen geordnet */
export function useSetupSections(): SetupSection[] {
  const { can, hasModule, session } = useSession();
  const order = (s: SetupSection) => SETUP_GROUPS.findIndex((g) => g.key === s.group);
  return SETUP_SECTIONS.filter((s) => hasModule(s.module) && can(s.permission) && (!s.requires || [s.requires].flat().some((m) => hasModule(m))) && (!s.industry || s.industry === session?.tenant.industry)).sort((a, b) => order(a) - order(b));
}

/** Die sichtbaren Bereiche je Gruppe, leere Gruppen fallen weg */
export function groupSections(sections: SetupSection[]): { key: SetupGroup; label: string; sections: SetupSection[] }[] {
  return SETUP_GROUPS.map((g) => ({ key: g.key, label: g.label, sections: sections.filter((s) => s.group === g.key) })).filter((g) => g.sections.length > 0);
}

/** Klein geschrieben und ohne Akzente, damit „gebuhr“ auch „Gebühr“ findet */
export const fold = (text: string) => text.toLocaleLowerCase('de-DE').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');

/** Suche in der Einrichtung: Jedes Wort muss in Bezeichnung, Beschreibung, Gruppe oder Stichwörtern vorkommen */
export function searchSections(sections: SetupSection[], query: string): SetupSection[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return sections;
  return sections.filter((s) => {
    const text = fold([s.label, s.description, SETUP_GROUPS.find((g) => g.key === s.group)?.label ?? '', ...(s.keywords ?? [])].join(' '));
    return words.every((w) => text.includes(w));
  });
}
